import React from 'react'
import { renderToString } from 'react-dom/server'
import mapboxgl from 'mapbox-gl'
import { Tent, Calendar, Route, Trophy, Mountain, Clock } from 'lucide-react'
import { UnifiedRoute } from '../../../services/firebase/routes'
import { simplifyCoordinatesForZoom } from '../../../utils/routeSimplification'
import { 
  getRouteCoordinates,
  getTotalDistance,
  getTotalElevationGain,
  calculateUnpavedPercentage,
  getRouteTypeDisplay,
  formatRouteLocation,
  getRouteRegion,
  getRouteStageCount
} from '../../../utils/routeDisplay'
import { quickTimeEstimate } from '../../../utils/timeCalculator'

interface RenderStaticRoutesParams {
  map: mapboxgl.Map
  publishedRoutes: UnifiedRoute[]
  showMarkers: boolean
  onRouteSelect: (route: any) => void
}

export function renderStaticRoutes({
  map,
  publishedRoutes,
  showMarkers,
  onRouteSelect
}: RenderStaticRoutesParams) {
  
  const staticSourceId = 'embed-static-routes'
  const staticLayerId = 'embed-static-routes-layer'
  
  // Remove existing static layers if any
  try {
    const layersToRemove = [
      'embed-static-routes-layer',
      'embed-static-routes-stroke',
      'embed-static-routes-shadow',
      'embed-static-routes-inner-emboss',
      'embed-static-unpaved-layer',
      'embed-static-routes-hit-area'
    ]
    
    layersToRemove.forEach(layerId => {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId)
      }
    })
    
    const sourcesToRemove = [
      'embed-static-routes',
      'embed-static-unpaved'
    ]
    
    sourcesToRemove.forEach(sourceId => {
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId)
      }
    })
  } catch (e) {
    console.warn('Failed to remove existing layers:', e)
  }
  
  // Create features for all routes
  const allRouteFeatures: any[] = []
  const allUnpavedFeatures: any[] = []
  
  publishedRoutes.forEach(route => {
    // For single segment routes, get color from route level
    // For multi-segment routes, we'll handle colors per segment below
    const defaultColor = '#ef4444'
    
    
    // Handle single segment routes
    if (route.coordinates?.length > 0) {
      
      // Don't simplify in embed mode - we want full detail
      const simplifiedCoords = route.coordinates
      
      // Skip if we don't have enough coordinates for a line
      if (simplifiedCoords.length >= 2) {
        allRouteFeatures.push({
          type: 'Feature',
          properties: {
            routeId: route.id,
            color: route.color || defaultColor,
            name: route.name
          },
          geometry: {
            type: 'LineString',
            coordinates: simplifiedCoords
          }
        })
      }
      
      // Add unpaved sections if available
      if (route.unpavedSegments?.length > 0) {
        route.unpavedSegments.forEach((unpavedSeg: any, index: number) => {
          if (unpavedSeg?.length > 0) {
            // Don't simplify in embed mode - we want full detail
            const simplifiedUnpavedCoords = unpavedSeg
            
            allUnpavedFeatures.push({
              type: 'Feature',
              properties: {
                routeId: `${route.id}-unpaved-${index}`,
                name: route.name
              },
              geometry: {
                type: 'LineString',
                coordinates: simplifiedUnpavedCoords
              }
            })
          }
        })
      } else {
        // Fallback for high unpaved percentage
        const unpavedPercentage = calculateUnpavedPercentage(route)
        if (unpavedPercentage > 50) {
          allUnpavedFeatures.push({
            type: 'Feature',
            properties: {
              routeId: `${route.id}-unpaved-fallback`,
              name: route.name,
              unpavedPercentage: unpavedPercentage
            },
            geometry: {
              type: 'LineString',
              coordinates: simplifiedCoords
            }
          })
        }
      }
    }
    
    // Handle multi-segment routes
    if (route.routes?.length > 0) {
      route.routes.forEach((segment: any, segmentIndex: number) => {
        // Skip excluded segments (e.g., optional or train link segments)
        if (segment.excludeFromMaster) {
          return;
        }
        
        if (segment.coordinates?.length > 0) {
          // Don't simplify in embed mode - we want full detail
          const simplifiedCoords = segment.coordinates
          
          if (simplifiedCoords.length >= 2) {
            allRouteFeatures.push({
              type: 'Feature',
              properties: {
                routeId: `${route.id}-segment-${segmentIndex}`,
                color: segment.color || defaultColor,
                name: route.name
              },
              geometry: {
                type: 'LineString',
                coordinates: simplifiedCoords
              }
            })
          }
          
          // Add unpaved sections for this segment
          if (segment.unpavedSegments?.length > 0) {
            segment.unpavedSegments.forEach((unpavedSeg: any, unpavedIndex: number) => {
              if (unpavedSeg?.length > 0) {
                // Don't simplify in embed mode - we want full detail
                const simplifiedUnpavedCoords = unpavedSeg
                
                allUnpavedFeatures.push({
                  type: 'Feature',
                  properties: {
                    routeId: `${route.id}-segment-${segmentIndex}-unpaved-${unpavedIndex}`,
                    name: route.name
                  },
                  geometry: {
                    type: 'LineString',
                    coordinates: simplifiedUnpavedCoords
                  }
                })
              }
            })
          } else {
            // Fallback for high unpaved percentage
            let segmentUnpavedPercentage = 0
            if (segment.stats?.surfaceBreakdown?.unpaved?.percentage !== undefined) {
              segmentUnpavedPercentage = segment.stats.surfaceBreakdown.unpaved.percentage
            } else if (segment.stats?.unpaved !== undefined) {
              segmentUnpavedPercentage = segment.stats.unpaved
            }
            
            if (segmentUnpavedPercentage > 50) {
              allUnpavedFeatures.push({
                type: 'Feature',
                properties: {
                  routeId: `${route.id}-segment-${segmentIndex}-unpaved-fallback`,
                  name: route.name,
                  unpavedPercentage: segmentUnpavedPercentage
                },
                geometry: {
                  type: 'LineString',
                  coordinates: simplifiedCoords
                }
              })
            }
          }
        }
      })
    }
  })
  
  
  // Add single source with all routes
  map.addSource(staticSourceId, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: allRouteFeatures
    }
  })
  
  // Add subtle shadow layer for emboss effect (very bottom)
  const shadowLayerId = 'embed-static-routes-shadow'
  try {
    map.addLayer({
      id: shadowLayerId,
      type: 'line',
      source: staticSourceId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
        'line-miter-limit': 1  // Limit how far corners can extend
      },
      paint: {
        'line-color': '#ffffff',
        'line-width': 5,  // Same as stroke width
        'line-opacity': 0.8,  // White needs higher opacity to be visible
        'line-blur': 0,  // No blur - solid line
        'line-offset': 1  // Closer offset
      }
    })
  } catch (error) {
    console.error('❌ Failed to add shadow layer:', error)
  }
  
  // Add white stroke layer first (on the bottom)
  const strokeLayerId = 'embed-static-routes-stroke'
  try {
    map.addLayer({
    id: strokeLayerId,
    type: 'line',
    source: staticSourceId,
    layout: {
      'line-join': 'round',
      'line-cap': 'round'
    },
    paint: {
      'line-color': '#ffffff',
      'line-width': 5,
      'line-opacity': 1
    }
  })
  } catch (error) {
    console.error('❌ Failed to add stroke layer:', error)
  }

  // Add static route layer with data-driven styling (on top of stroke)
  try {
    map.addLayer({
    id: staticLayerId,
    type: 'line',
    source: staticSourceId,
    layout: {
      'line-join': 'round',
      'line-cap': 'round'
    },
    paint: {
      'line-color': ['get', 'color'],
      'line-width': 3,
      'line-opacity': 0.8
    }
  })
  } catch (error) {
    console.error('❌ Failed to add route layer:', error)
  }
  
  // Add unpaved layer if we have unpaved sections
  if (allUnpavedFeatures.length > 0) {
    const unpavedSourceId = 'embed-static-unpaved'
    const unpavedLayerId = 'embed-static-unpaved-layer'
    
    try {
      map.addSource(unpavedSourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: allUnpavedFeatures
      }
    })
    } catch (error) {
      console.error('❌ Failed to add unpaved source:', error)
      return
    }
    
    // Add unpaved overlay layer (dotted white lines) - explicitly above the route layer
    try {
      map.addLayer({
      id: unpavedLayerId,
      type: 'line',
      source: unpavedSourceId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#ffffff',
        'line-width': 2,
        'line-opacity': 0.8,
        'line-dasharray': [0.5, 2]
      }
    })
    } catch (error) {
      console.error('❌ Failed to add unpaved layer:', error)
    }
    
  }
  
  // Add invisible hit area layer for better hover detection
  const hitAreaLayerId = 'embed-static-routes-hit-area'
  try {
    map.addLayer({
      id: hitAreaLayerId,
      type: 'line',
      source: staticSourceId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': 'transparent',
        'line-width': 20, // Much wider for easier hovering
        'line-opacity': 0
      }
    })
  } catch (error) {
    console.error('❌ Failed to add hit area layer:', error)
  }
  
  // Add hover interactions - use the hit area layer for better hover detection
  addStaticRouteInteractions(map, publishedRoutes, showMarkers, onRouteSelect, hitAreaLayerId)
}

function addStaticRouteInteractions(
  map: mapboxgl.Map,
  publishedRoutes: UnifiedRoute[],
  showMarkers: boolean,
  onRouteSelect: (route: any) => void,
  staticLayerId: string
) {
  
  let currentStaticHover: string | null = null
  let cleanupTimeout: NodeJS.Timeout | null = null
  let staticHoverPanel: HTMLElement | null = null
  const strokeLayerId = 'embed-static-routes-stroke'
  const routeLayerId = 'embed-static-routes-layer'
  const supportsHover = typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches
  
  // Store reference to panel globally for cleanup
  ;(window as any).__staticHoverPanel = staticHoverPanel
  
  // Remove any existing event handlers before adding new ones
  try {
    map.off('mouseenter', staticLayerId)
    map.off('mouseleave', staticLayerId)
    map.off('click', staticLayerId)
  } catch (e) {
    // Ignore if handlers don't exist
  }
  
  // Add hover effect to main route layer
  if (supportsHover) {
    map.on('mouseenter', staticLayerId, (e) => {
      // Clean up any existing static hover panel first (only if switching from no markers to markers)
      if (showMarkers) {
        const existingStaticPanel = (window as any).__staticHoverPanel
        if (existingStaticPanel && existingStaticPanel.parentNode) {
          existingStaticPanel.remove()
          ;(window as any).__staticHoverPanel = null
        }
      }
      staticHoverPanel = null
      
      if (e.features && e.features[0]) {
        const routeId = e.features[0].properties?.routeId
        if (routeId) {
          const route = publishedRoutes.find(r => r.id === routeId || routeId.startsWith(r.id))
          if (route) {
            // Clear any pending cleanup
            if (cleanupTimeout) {
              clearTimeout(cleanupTimeout)
              cleanupTimeout = null
            }
            
            if (currentStaticHover !== route.id) {
              currentStaticHover = route.id
              
              // Make the hovered route lines thicker
              try {
                // For multi-segment routes, we need to match all segments
                const isSegment = routeId.includes('-segment-')
                const baseRouteId = isSegment ? routeId.split('-segment-')[0] : routeId
                
                // Increase stroke width
                map.setPaintProperty(strokeLayerId, 'line-width', [
                  'case',
                  ['any',
                    ['==', ['get', 'routeId'], routeId],
                    ['==', ['slice', ['get', 'routeId'], 0, baseRouteId.length], baseRouteId]
                  ],
                  8, // Hovered width (was 5)
                  5  // Default width
                ])
                
                // Increase main line width
                map.setPaintProperty(routeLayerId, 'line-width', [
                  'case',
                  ['any',
                    ['==', ['get', 'routeId'], routeId],
                    ['==', ['slice', ['get', 'routeId'], 0, baseRouteId.length], baseRouteId]
                  ],
                  5, // Hovered width (was 3)
                  3  // Default width
                ])
              } catch (err) {
                // Ignore if layers don't exist
              }
              
              if (showMarkers) {
                // If markers are shown, trigger marker hover
                const markerElement = document.querySelector(`[data-route-id="${route.id}"]`) as HTMLElement
                if (markerElement) {
                  const mouseEnterEvent = new MouseEvent('mouseenter', {
                    bubbles: true,
                    cancelable: true,
                    view: window
                  })
                  markerElement.dispatchEvent(mouseEnterEvent)
                }
              } else {
                // If no markers, create our own hover panel
                staticHoverPanel = createStaticHoverPanel(route, e.point, map, onRouteSelect, cleanupTimeout, currentStaticHover)
              }
            }
          }
        }
      }
    })
  }
  
  // Add mouseleave to clean up hover effects
  if (supportsHover) {
    map.on('mouseleave', staticLayerId, () => {
      // Use a short timeout to allow for mouse movement between overlapping elements
      cleanupTimeout = setTimeout(() => {
        if (currentStaticHover) {
          // Reset line widths to default
          try {
            map.setPaintProperty(strokeLayerId, 'line-width', 5)
            map.setPaintProperty(routeLayerId, 'line-width', 3)
          } catch (err) {
            // Ignore if layers don't exist
          }
          
          if (showMarkers) {
            // If markers are shown, trigger marker mouseleave
            // Also clean up any static hover panel that might exist
            if (staticHoverPanel) {
              staticHoverPanel.remove()
              staticHoverPanel = null
            }
            
            const markerElement = document.querySelector(`[data-route-id="${currentStaticHover}"]`) as HTMLElement
            if (markerElement) {
              const mouseLeaveEvent = new MouseEvent('mouseleave', {
                bubbles: true,
                cancelable: true,
                view: window
              })
              markerElement.dispatchEvent(mouseLeaveEvent)
            }
          } else {
            // If no markers, remove our custom panel
            if (staticHoverPanel) {
              staticHoverPanel.remove()
              staticHoverPanel = null
              ;(window as any).__staticHoverPanel = null
            }
          }
          
          currentStaticHover = null
        }
        cleanupTimeout = null
      }, 50) // Very short delay to allow for mouse movement
    })
  }
  
  // Change cursor on hover (only for hover-capable devices)
  if (supportsHover) {
    map.on('mouseenter', staticLayerId, () => {
      map.getCanvas().style.cursor = 'pointer'
    })
    
    map.on('mouseleave', staticLayerId, () => {
      map.getCanvas().style.cursor = ''
    })
  }
  
  // Add click handler for static routes
  map.on('click', staticLayerId, (e) => {
    if (e.features && e.features[0]) {
      const routeId = e.features[0].properties?.routeId
      if (routeId) {
        const route = publishedRoutes.find(r => r.id === routeId || routeId.startsWith(r.id))
        if (route) {
          // Clean up ALL hover panels globally
          const allHoverPanels = document.querySelectorAll('.route-hover-panel-container, .static-route-hover-panel')
          allHoverPanels.forEach(panel => panel.remove())
          
          // Clean up any static hover panels
          if (staticHoverPanel) {
            staticHoverPanel.remove()
            staticHoverPanel = null
            ;(window as any).__staticHoverPanel = null
          }
          
          // Call onRouteSelect to show RoutePopupCard without panning
          onRouteSelect({
            id: route.id,
            name: route.name,
            routeType: getRouteTypeDisplay(route.routeType),
            coordinates: route.coordinates || [],
            location: formatRouteLocation(route),
            region: getRouteRegion(route),
            distance: getTotalDistance(route),
            elevationGain: getTotalElevationGain(route),
            surface: `${100 - calculateUnpavedPercentage(route)}% paved`,
            unpavedPercentage: calculateUnpavedPercentage(route),
            description: route.description || '',
            creator: route.attribution?.name || route.ownerDisplayName || route.ownerId,
            creatorPhoto: route.ownerPhoto || null,
            organization: route.submission?.clubName || 'Personal Route',
            clubLogo: (route as any).clubLogo || null,
            attribution: route.attribution,
            images: (() => {
              const images: string[] = []
              // Add main highlight image first
              if (route.highlightImages?.main && route.photos) {
                const mainPhoto = route.photos.find(p => p.id === route.highlightImages!.main)
                if (mainPhoto?.url) images.push(mainPhoto.url)
              }
              // Add additional highlight images
              if (route.highlightImages?.additional && route.photos) {
                route.highlightImages.additional.forEach(photoId => {
                  const photo = route.photos?.find(p => p.id === photoId)
                  if (photo?.url) images.push(photo.url)
                })
              }
              // If no highlight images, fallback to first few photos
              if (images.length === 0 && route.photos && route.photos.length > 0) {
                images.push(...route.photos.slice(0, 5).map(p => p.url))
              }
              return images
            })(),
            thumbnail: route.thumbnail || null,
            firebaseRoute: route
          })
        }
      }
    }
  })
}

// Function to create hover panel for static routes (when no markers)
function createStaticHoverPanel(
  route: UnifiedRoute,
  point: mapboxgl.Point,
  map: mapboxgl.Map,
  onRouteSelect: (route: any) => void,
  cleanupTimeout: NodeJS.Timeout | null,
  currentStaticHover: string | null
): HTMLElement {
  // Remove existing panel
  const existingGlobalPanel = (window as any).__staticHoverPanel
  if (existingGlobalPanel && existingGlobalPanel.parentNode) {
    existingGlobalPanel.remove()
  }
  
  const staticHoverPanel = document.createElement('div')
  staticHoverPanel.className = 'route-cluster-hover-panel static-route-hover-panel'
  staticHoverPanel.setAttribute('data-static-hover', 'true')
  staticHoverPanel.style.cssText = `
    position: fixed;
    background: rgba(31, 41, 55, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 8px;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    pointer-events: auto;
    backdrop-filter: blur(4px);
    display: flex;
    flex-direction: column;
    gap: 8px;
  `
  
  // Create preview content matching marker hover panel exactly
  const routePreview = document.createElement('div')
  routePreview.className = 'route-preview-item'
  routePreview.style.cssText = `
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    background: rgba(55, 65, 81, 0.2);
  `
  
  // Route type icon container - matching marker hover exactly
  const iconContainer = document.createElement('div')
  iconContainer.style.cssText = `
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  `
  
  const routeTypeDisplay = getRouteTypeDisplay(route.routeType)
  iconContainer.innerHTML = (() => {
    switch (routeTypeDisplay) {
      case 'Bikepacking': return renderToString(<Tent size={16} stroke="white" strokeWidth={2.5} />)
      case 'Event': return renderToString(<Calendar size={16} stroke="white" strokeWidth={2.5} />)
      case 'Permanent': return renderToString(<Trophy size={16} stroke="white" strokeWidth={2.5} />)
      default: return renderToString(<Route size={16} stroke="white" strokeWidth={2.5} />)
    }
  })()
  
  // Info container
  const infoContainer = document.createElement('div')
  infoContainer.style.cssText = 'flex: 1; min-width: 0;'
  
  // Route name
  const routeName = document.createElement('div')
  routeName.style.cssText = `
    font-size: 13px;
    font-weight: 600;
    color: white;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 4px;
  `
  routeName.textContent = route.name
  
  // Route stats - matching marker hover exactly
  const routeStats = document.createElement('div')
  routeStats.style.cssText = 'display: flex; align-items: center; gap: 8px; font-size: 11px; color: #9ca3af;'
  
  // Get formatted distance like the marker hover
  const distance = getTotalDistance(route)
  const formattedDistance = distance >= 1000 
    ? (distance / 1000).toFixed(1) + 'k'
    : Math.ceil(distance).toString()
  
  const elevation = getTotalElevationGain(route)
  const unpavedPercentage = calculateUnpavedPercentage(route)
  const stagesCount = getRouteStageCount(route)
  
  // Distance stat
  const distanceStat = document.createElement('div')
  distanceStat.style.cssText = 'display: flex; align-items: center; gap: 4px;'
  distanceStat.innerHTML = `
    ${renderToString(<Route className="w-2.5 h-2.5 text-emerald-400" />)}
    <span>${formattedDistance}km</span>
  `
  
  // Elevation stat  
  const elevationStat = document.createElement('div')
  elevationStat.style.cssText = 'display: flex; align-items: center; gap: 4px; margin-left: 4px;'
  elevationStat.innerHTML = `
    ${renderToString(<Mountain className="w-2.5 h-2.5 text-emerald-400" />)}
    <span>${elevation.toLocaleString()}m</span>
  `
  
  // Unpaved stat
  const unpavedStat = document.createElement('div')
  unpavedStat.style.cssText = 'display: flex; align-items: center; gap: 4px;'
  unpavedStat.innerHTML = `
    <svg class="w-2.5 h-2.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
    <span>${unpavedPercentage}% unp.</span>
  `
  
  // Stages/Time stat
  const stagesStat = document.createElement('div')
  stagesStat.style.cssText = 'display: flex; align-items: center; gap: 4px; margin-left: 4px;'
  
  if (stagesCount === 1) {
    // Show time for single stage routes
    const routeTypeDisplay = getRouteTypeDisplay(route.routeType)
    stagesStat.innerHTML = `
      ${renderToString(<Clock className="w-2.5 h-2.5 text-emerald-400" />)}
      <span>${quickTimeEstimate(distance, elevation, unpavedPercentage, routeTypeDisplay)}</span>
    `
  } else {
    // Show stages for multi-stage routes
    stagesStat.innerHTML = `
      <svg class="w-2.5 h-2.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
      <span>${stagesCount} stages</span>
    `
  }
  
  routeStats.appendChild(distanceStat)
  routeStats.appendChild(elevationStat)
  routeStats.appendChild(unpavedStat)
  routeStats.appendChild(stagesStat)
  
  infoContainer.appendChild(routeName)
  infoContainer.appendChild(routeStats)
  
  routePreview.appendChild(iconContainer)
  routePreview.appendChild(infoContainer)
  
  // Add hover effects
  routePreview.addEventListener('mouseenter', () => {
    routePreview.style.background = 'rgba(16, 185, 129, 0.2)'
  })
  
  routePreview.addEventListener('mouseleave', () => {
    routePreview.style.background = 'rgba(55, 65, 81, 0.2)'
  })
  
  // Add click handler - same as marker click
  routePreview.addEventListener('click', () => {
    // Clean up ALL hover panels globally
    const allHoverPanels = document.querySelectorAll('.route-hover-panel-container, .static-route-hover-panel')
    allHoverPanels.forEach(panel => panel.remove())
    
    // Hide this panel immediately when clicked
    staticHoverPanel.remove()
    ;(window as any).__staticHoverPanel = null
    
    // Call onRouteSelect to show RoutePopupCard without panning - same as marker click
    onRouteSelect({
      id: route.id,
      name: route.name,
      routeType: getRouteTypeDisplay(route.routeType),
      coordinates: route.coordinates || [],
      location: formatRouteLocation(route),
      region: getRouteRegion(route),
      distance: getTotalDistance(route),
      elevationGain: getTotalElevationGain(route),
      surface: `${100 - calculateUnpavedPercentage(route)}% paved`,
      unpavedPercentage: calculateUnpavedPercentage(route),
      description: route.description || '',
      creator: route.attribution?.name || route.ownerDisplayName || route.ownerId,
      creatorPhoto: route.ownerPhoto || null,
      organization: route.submission?.clubName || 'Personal Route',
      clubLogo: (route as any).clubLogo || null,
      attribution: route.attribution,
      images: (() => {
        const images: string[] = []
        // Add main highlight image first
        if (route.highlightImages?.main && route.photos) {
          const mainPhoto = route.photos.find(p => p.id === route.highlightImages!.main)
          if (mainPhoto?.url) images.push(mainPhoto.url)
        }
        // Add additional highlight images
        if (route.highlightImages?.additional && route.photos) {
          route.highlightImages.additional.forEach(photoId => {
            const photo = route.photos?.find(p => p.id === photoId)
            if (photo?.url) images.push(photo.url)
          })
        }
        // If no highlight images, fallback to first few photos
        if (images.length === 0 && route.photos && route.photos.length > 0) {
          images.push(...route.photos.slice(0, 5).map(p => p.url))
        }
        return images
      })(),
      thumbnail: route.thumbnail || null,
      firebaseRoute: route
    })
  })
  
  staticHoverPanel.appendChild(routePreview)
  document.body.appendChild(staticHoverPanel)
  
  // Store global reference for cleanup
  ;(window as any).__staticHoverPanel = staticHoverPanel
  
  // Keep panel visible when hovering over it
  let isMouseOverStaticPanel = false
  
  staticHoverPanel.addEventListener('mouseenter', () => {
    isMouseOverStaticPanel = true
    if (cleanupTimeout) {
      clearTimeout(cleanupTimeout)
    }
  })
  
  staticHoverPanel.addEventListener('mouseleave', () => {
    isMouseOverStaticPanel = false
    // Use the same cleanup timeout logic as the static route mouseleave
    setTimeout(() => {
      if (!isMouseOverStaticPanel) {
        staticHoverPanel.remove()
        ;(window as any).__staticHoverPanel = null
      }
    }, 50)
  })
  
  // Position the panel near the mouse
  const rect = map.getContainer().getBoundingClientRect()
  const x = rect.left + point.x
  const y = rect.top + point.y
  
  // Position above the point initially
  let top = y - 100 // Estimate height
  let left = x - 150 // Center horizontally
  
  // Keep within viewport
  if (top < 10) {
    top = y + 20
  }
  if (left < 10) {
    left = 10
  } else if (left + 300 > window.innerWidth - 10) {
    left = window.innerWidth - 310
  }
  
  staticHoverPanel.style.left = `${left}px`
  staticHoverPanel.style.top = `${top}px`
  
  // Adjust position after content loads
  requestAnimationFrame(() => {
    if (staticHoverPanel) {
      const actualWidth = staticHoverPanel.offsetWidth
      const actualHeight = staticHoverPanel.offsetHeight
      let adjustedLeft = x - actualWidth / 2
      let adjustedTop = y - actualHeight - 10
      
      if (adjustedTop < 10) {
        adjustedTop = y + 20
      }
      if (adjustedLeft < 10) {
        adjustedLeft = 10
      } else if (adjustedLeft + actualWidth > window.innerWidth - 10) {
        adjustedLeft = window.innerWidth - actualWidth - 10
      }
      
      staticHoverPanel.style.left = `${adjustedLeft}px`
      staticHoverPanel.style.top = `${adjustedTop}px`
    }
  })
  
  return staticHoverPanel
}
